import { Component } from '@theme/component';

const MOBILE_MEDIA_QUERY = '(max-width: 989px)';

/**
 * Trust badges bar with auto-scroll ticker on viewports under 990px.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - Animated ticker wrapper.
 * @property {HTMLElement} content - Ticker content group.
 * @property {HTMLElement} track - Badge list.
 *
 * @extends {Component<Refs>}
 */
class CtrTrustBadgesBar extends Component {
  requiredRefs = ['wrapper', 'content', 'track'];

  /** @type {MediaQueryList} */
  #mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

  /** @type {() => void} */
  #handleViewportChange = () => {
    this.#reset();
    if (this.#mobileQuery.matches) {
      this.#enableTicker();
    }
  };

  connectedCallback() {
    super.connectedCallback();

    this.#mobileQuery.addEventListener('change', this.#handleViewportChange);
    this.#handleViewportChange();
  }

  disconnectedCallback() {
    this.#mobileQuery.removeEventListener('change', this.#handleViewportChange);
    this.#reset();
  }

  #reset() {
    const { wrapper, content, track } = this.refs;

    this.removeAttribute('data-marquee-active');
    this.style.removeProperty('--ctr-trust-badges-speed');

    for (const clone of content.querySelectorAll('.ctr-trust-badges__track:not(:first-child)')) {
      clone.remove();
    }

    for (const clone of wrapper.querySelectorAll('.ctr-trust-badges__content:not(:first-child)')) {
      clone.remove();
    }

    track.removeAttribute('aria-hidden');
    content.removeAttribute('aria-hidden');
  }

  async #enableTicker() {
    const numberOfCopies = await this.#queryNumberOfCopies();

    this.#addRepeatedTracks(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(this.#calculateSpeed(numberOfCopies));
    this.setAttribute('data-marquee-active', 'true');
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--ctr-trust-badges-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { track } = this.refs;

    return new Promise((resolve) => {
      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;

          intersectionObserver.disconnect();

          const { width: viewportWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: trackWidth } = firstEntry.boundingClientRect;

          setTimeout(() => {
            resolve(trackWidth === 0 ? 1 : Math.ceil(viewportWidth / trackWidth));
          }, 0);
        },
        { root: this }
      );

      intersectionObserver.observe(track);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    const gap = parseFloat(getComputedStyle(this.refs.wrapper).gap || '0');
    const halfWidth = this.refs.content.scrollWidth;
    const distancePx = halfWidth + gap / 2;
    const fallbackSeconds = Math.sqrt(numberOfCopies) * 20;
    const speed = distancePx > 0 ? distancePx / speedFactor : fallbackSeconds;

    return Math.max(10, speed);
  }

  #duplicateContent() {
    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    this.#stripRefs(clone);
    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedTracks(numberOfCopies) {
    const { content, track } = this.refs;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = /** @type {HTMLElement} */ (track.cloneNode(true));
      this.#stripRefs(clone);
      content.appendChild(clone);
    }
  }

  /**
   * @param {Element} root
   */
  #stripRefs(root) {
    root.removeAttribute('ref');

    for (const element of root.querySelectorAll('[ref]')) {
      element.removeAttribute('ref');
    }
  }
}

if (!customElements.get('ctr-trust-badges-bar')) {
  customElements.define('ctr-trust-badges-bar', CtrTrustBadgesBar);
}
